import type { CalEvent, EventInput } from '../shared/types';
import { addDays, toISO } from './dates';

const pad = (n: number): string => String(n).padStart(2, '0');

// --- Import ------------------------------------------------------------------
interface Prop {
  params: string;
  value: string;
}
type Props = Record<string, Prop[]>;

function unfold(text: string): string[] {
  const out: string[] = [];
  for (const line of text.replace(/\r\n|\r/g, '\n').split('\n')) {
    if ((line.startsWith(' ') || line.startsWith('\t')) && out.length > 0) out[out.length - 1] += line.slice(1);
    else out.push(line);
  }
  return out;
}

function unescapeText(s: string): string {
  return s.replace(/\\([nN,;\\])/g, (_m, c: string) => (c === 'n' || c === 'N' ? '\n' : c));
}

function parseDt(v: string): { date: string; time?: string } | null {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?(Z)?)?$/.exec(v.trim());
  if (!m) return null;
  if (m[4] === undefined) return { date: `${m[1]}-${m[2]}-${m[3]}` };
  if (m[7]) {
    // UTC -> lokale Zeit
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? '0')));
    return { date: toISO(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
  }
  return { date: `${m[1]}-${m[2]}-${m[3]}`, time: `${m[4]}:${m[5]}` };
}

const formatDe = (date: string): string => {
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}`;
};

const firstVal = (p: Props, name: string): string => {
  const v = p[name]?.[0]?.value;
  return v ? unescapeText(v).trim() : '';
};
const allVals = (p: Props, name: string): string[] =>
  (p[name] ?? []).map((x) => unescapeText(x.value).trim()).filter(Boolean);

/** Person aus ORGANIZER/ATTENDEE: „Name (mail)“ bzw. nur Name oder Adresse. */
function personOf(prop: Prop): string {
  const cn = /CN=(?:"([^"]*)"|([^;:]*))/i.exec(prop.params);
  const name = cn ? (cn[1] ?? cn[2] ?? '').trim() : '';
  const mail = prop.value.replace(/^mailto:/i, '').trim();
  return name && mail && name !== mail ? `${name} (${mail})` : name || mail;
}

// Bereits einzeln behandelte X-Eigenschaften und rein technische X-Präfixe (werden nicht übernommen)
const HANDLED_X = new Set(['X-LOCATION-NAME', 'X-STREET-ADDRESS', 'X-POSTAL-CODE', 'X-CITY', 'X-COUNTRY']);
const TECHNICAL_X_PREFIXES = [
  'X-MICROSOFT-', 'X-APPLE-', 'X-WR-', 'X-GOOGLE-', 'X-MOZ-', 'X-LIC-', 'X-ALT-DESC', 'X-EVOLUTION-', 'X-ENTOURAGE', 'X-PUBLISHED-TTL',
];

function xLabel(name: string): string {
  return name
    .slice(2)
    .toLowerCase()
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Baut aus allen für Nutzer interessanten Eigenschaften den Text „Name: Wert“ (Beschreibung zuletzt). */
function buildDescription(p: Props, start: { date: string; time?: string }): string | undefined {
  const lines: string[] = [];
  const add = (label: string, value: string): void => {
    if (value) lines.push(`${label}: ${value}`);
  };

  add('Ort', firstVal(p, 'X-LOCATION-NAME') || firstVal(p, 'LOCATION'));
  add('Straße', firstVal(p, 'X-STREET-ADDRESS'));
  add('PLZ', firstVal(p, 'X-POSTAL-CODE'));
  add('Stadt', firstVal(p, 'X-CITY'));
  add('Land', firstVal(p, 'X-COUNTRY'));
  add('Koordinaten', firstVal(p, 'GEO').replace(';', ', '));

  const endRaw = p['DTEND']?.[0];
  const end = endRaw ? parseDt(endRaw.value) : null;
  if (end) {
    if (start.time === undefined) {
      // ganztägig: DTEND ist exklusiv
      const last = addDays(end.date, -1);
      if (last > start.date) add('Ende', formatDe(last));
    } else if (end.time) {
      add('Ende', end.date === start.date ? end.time : `${formatDe(end.date)} ${end.time}`);
    }
  }

  const organizer = p['ORGANIZER']?.[0];
  add('Organisator', organizer ? personOf(organizer) : '');
  add('Teilnehmer', (p['ATTENDEE'] ?? []).map(personOf).filter(Boolean).join(', '));
  add('Kategorien', allVals(p, 'CATEGORIES').join(', '));
  const status = firstVal(p, 'STATUS');
  if (status && status.toUpperCase() !== 'CONFIRMED') add('Status', status);
  add('Kontakt', allVals(p, 'CONTACT').join('; '));
  add('Link', firstVal(p, 'URL'));
  add('Kommentar', allVals(p, 'COMMENT').join('\n'));

  for (const key of Object.keys(p)) {
    if (!key.startsWith('X-') || HANDLED_X.has(key) || TECHNICAL_X_PREFIXES.some((pre) => key.startsWith(pre))) continue;
    add(xLabel(key), firstVal(p, key));
  }

  add('Beschreibung', firstVal(p, 'DESCRIPTION')); // immer als Letztes
  return lines.length ? lines.join('\n').slice(0, 5000) : undefined;
}

function build(p: Props): EventInput | null {
  const startRaw = p['DTSTART']?.[0];
  if (!startRaw) return null;
  const start = parseDt(startRaw.value);
  if (!start) return null;
  return {
    title: (firstVal(p, 'SUMMARY') || '(ohne Titel)').slice(0, 200),
    date: start.date,
    time: start.time,
    description: buildDescription(p, start),
    uid: p['UID']?.[0]?.value.trim() || undefined,
    source: 'ics',
  };
}

/** Liest alle VEVENTs einer ICS-Datei. Wiederholungsregeln (RRULE) werden nicht aufgelöst. */
export function parseIcs(text: string): EventInput[] {
  const events: EventInput[] = [];
  let cur: Props | null = null;
  let sub = 0; // Verschachtelung (z. B. VALARM) innerhalb eines VEVENT

  for (const line of unfold(text)) {
    if (line === 'BEGIN:VEVENT') {
      cur = {};
      sub = 0;
      continue;
    }
    if (line === 'END:VEVENT') {
      if (cur) {
        const ev = build(cur);
        if (ev) events.push(ev);
      }
      cur = null;
      continue;
    }
    if (!cur) continue;
    if (line.startsWith('BEGIN:')) {
      sub++;
      continue;
    }
    if (line.startsWith('END:')) {
      sub = Math.max(0, sub - 1);
      continue;
    }
    if (sub > 0) continue;

    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const left = line.slice(0, colon);
    const semi = left.indexOf(';');
    const name = (semi < 0 ? left : left.slice(0, semi)).toUpperCase();
    (cur[name] ??= []).push({ params: semi < 0 ? '' : left.slice(semi + 1), value: line.slice(colon + 1) });
  }
  return events;
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// --- Export ------------------------------------------------------------------
function esc(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

function fold(line: string): string {
  const out: string[] = [];
  let rest = line;
  while (rest.length > 74) {
    out.push(rest.slice(0, 74));
    rest = ` ${rest.slice(74)}`;
  }
  out.push(rest);
  return out.join('\r\n');
}

export function buildIcs(events: CalEvent[]): string {
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const lines: string[] = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Franks Kalender//DE', 'CALSCALE:GREGORIAN'];
  for (const e of events) {
    const day = e.date.replace(/-/g, '');
    lines.push('BEGIN:VEVENT', `UID:${e.id}@franks-kalender`, `DTSTAMP:${stamp}`);
    if (e.time) {
      const [hh, mm] = e.time.split(':').map(Number);
      const end = new Date(2000, 0, 1, hh + 1, mm);
      const endDay = e.date.replace(/-/g, '');
      lines.push(`DTSTART:${day}T${pad(hh)}${pad(mm)}00`);
      // Ende = Start + 1 Stunde (Überlauf über Mitternacht wird vereinfacht auf 23:59 gekappt)
      lines.push(end.getDate() === 1 ? `DTEND:${endDay}T${pad(end.getHours())}${pad(end.getMinutes())}00` : `DTEND:${endDay}T235900`);
    } else {
      lines.push(`DTSTART;VALUE=DATE:${day}`);
    }
    lines.push(`SUMMARY:${esc(e.title)}`);
    if (e.description) lines.push(`DESCRIPTION:${esc(e.description)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return `${lines.map(fold).join('\r\n')}\r\n`;
}
